'use client';

import React, { useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { ExpendableDataTableViewOptions } from './column-toggle';
import { ExpendableDataTableColumnHeader } from './data-table-column-header';
import { ExpendableDataTablePagination } from './data-table-pagination';

/**
 * @file data-table.jsx
 * @description Expandable data table component
 * @version 0.0.2
 * Created at: 02/09/2025
 * Updated at: 02/09/2025
 */
export const ExpendableDataTable = ({
  columns,
  data,
  noIndexCol = true,
  className,
  expandableRowRender,
  expandableRowData,
  defaultExpandedRows = [],
}) => {
  //-------------- State & Variables --------------//
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set(defaultExpandedRows));

  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const enhancedColumns = [
    ...(expandableRowRender
      ? [
          {
            id: 'expand',
            header: '',
            cell: ({ row }) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleRowExpansion(row.id)}
                className="h-8 w-8 p-0"
              >
                {expandedRows.has(row.id) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            ),
            size: 40,
          },
        ]
      : []),
    {
      id: 'index',
      header: ({ column }) => <ExpendableDataTableColumnHeader column={column} title="SR No." />,
      cell: (info) => info.row.index + 1,
      size: 20,
    },
    ...columns,
  ];

  const table = useReactTable({
    data,
    columns: noIndexCol
      ? expandableRowRender
        ? [enhancedColumns[0], ...columns]
        : columns
      : enhancedColumns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),

    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    onColumnFiltersChange: setColumnFilters,

    // Global filter
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',

    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 12,
      },
    },
  });
  //-------------- Use Effects --------------//

  //-------------- Other Methods --------------//

  return (
    <div className={cn('w-full space-y-4', className)}>
      <ExpendableDataTableViewOptions
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <div className="w-full overflow-hidden rounded-md border">
        <Table className={'relative'}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className={'pl-4!'}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandableRowRender && expandedRows.has(row.id) && (
                    <TableRow>
                      <TableCell colSpan={table.getAllColumns().length} className="border-b-0 p-0">
                        <div className="bg-muted/30 px-4 py-3">
                          {expandableRowRender(
                            row.original,
                            expandableRowData?.[row.index] || expandableRowData?.[row.id]
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <ExpendableDataTablePagination table={table} />
    </div>
  );
};
